import { t } from '../i18n/t';
import { tr } from '../i18n/tr';
import type { components } from './schema';

type ApiErrorFields = Pick<ApiError, 'status' | 'type' | 'details' | 'retryAfterS' | 'traceId'>;

// A failed call (UI architecture §4 Errors). `message` is the Turkish text from the catalog, by
// `type` first, then by status class; the server's own `message` is never shown.
export class ApiError extends Error {
  readonly status: number; // 0: the request got no answer
  readonly type: string | null; // null for a network failure or a body without the envelope
  readonly details: ReadonlyArray<components['schemas']['ErrorDetail']>;
  readonly retryAfterS: number | null;
  readonly traceId: string | null; // the X-Trace-Id response header

  constructor(fields: ApiErrorFields) {
    super(turkishMessage(fields));
    this.name = 'ApiError';
    this.status = fields.status;
    this.type = fields.type;
    this.details = fields.details;
    this.retryAfterS = fields.retryAfterS;
    this.traceId = fields.traceId;
  }
}

// Whether `key` names an entry of a catalog branch, e.g. a known error type.
export function isKeyOf<T extends object>(branch: T, key: string): key is Extract<keyof T, string> {
  return Object.hasOwn(branch, key);
}

function turkishMessage({ status, type, details, retryAfterS }: ApiErrorFields): string {
  if (status === 0) return t('errors.status.network');
  // The wait in plain digits ("2400 saniye"); t() would group a number (2.400).
  if (retryAfterS !== null && type !== null && isKeyOf(tr.errors.wait, type)) {
    return t(`errors.wait.${type}`, { n: String(retryAfterS) });
  }
  // A known type's placeholders take the facts of `details` by path, e.g. {error_code}.
  const facts = Object.fromEntries(details.map((detail) => [detail.path, detail.code]));
  const text =
    type !== null && isKeyOf(tr.errors.type, type)
      ? t(`errors.type.${type}`, facts)
      : t(status >= 500 ? 'errors.status.5xx' : 'errors.status.4xx');
  return retryAfterS === null
    ? text
    : `${text} ${t('errors.retryAfter', { n: String(retryAfterS) })}`;
}

// The ApiError of a non-2xx response: the error envelope when the body is one, else the status alone.
export async function responseError(response: Response): Promise<ApiError> {
  const body: unknown = await response.json().catch(() => null);
  const envelope = (body as Partial<components['schemas']['Error']> | null)?.error;
  const error = typeof envelope?.type === 'string' ? envelope : undefined;
  return new ApiError({
    status: response.status,
    type: error?.type ?? null,
    details: error?.details ?? [],
    retryAfterS: error?.retry_after_s ?? null,
    traceId: response.headers.get('X-Trace-Id'),
  });
}
